/**
 * EditableItem — positions a child at (x, y) and overlays edit chrome.
 *
 * In edit mode pointer-down on the wrapper is either a click or a drag,
 * decided by movement during the press:
 *   - moved ≤ CLICK_THRESHOLD_PX → it's a click. Selects this item; the
 *     bottom InspectorPanel slides up with its fields.
 *   - moved >  CLICK_THRESHOLD_PX → it's a drag. Moves the item, snapping
 *     back if the drop would overlap a neighbor.
 *
 * Threshold scales by pointer type — mouse is 4px (tight), touch/pen is
 * 10px (avoids treating a finger jitter as a drag).
 *
 * Resize: 8 invisible edge/corner hit zones overlay the item. Sides (n/s
 * /e/w) resize a single axis; corners (ne/nw/se/sw) resize both. Cursor
 * changes on hover to signal the affordance. Replaces the earlier
 * ⤡ corner glyph — user 2026-07-03 preferred the standard desktop-editor
 * pattern (Figma, Illustrator, browser dev tools) over a visible icon.
 * ButtonGrid still opts out (its size changes via rows/cols steppers).
 *
 * In test mode the handles are hidden, pointer events go straight through
 * to the child (so the button taps normally), and no click-vs-drag logic
 * runs.
 *
 * Right-click (mouse contextmenu) / two-finger tap: opens the ITEM CONTEXT
 * MENU for this item in both play and edit modes — Inspect / Move / Cut /
 * Copy / Paste / Duplicate / Delete. Until 2026-08-09 the same gesture
 * called `selectItem()` and slid the InspectorPanel straight up; the
 * operator asked for it not to, and Inspect is now one row of the menu
 * instead of the gesture's whole meaning. The escape-hatch property is
 * unchanged: none of it flips edit mode, so live performance can pause for a
 * tweak without losing posture.
 */

import { batch, createMemo, createSignal, onMount, onCleanup, For, Show, type JSX } from 'solid-js';
import type { LayoutItem } from '../layout/store.ts';
import {
  isEditMode, moveAndResizeItem, resizeItem, wouldOverlap,
  selectedItemId, selectItem, isRatioLocked, editItem,
  isItemSelected, toggleItemSelected, selectedItemIds, moveItems, layoutItems,
  highlightItem, inspectorItem,
  groupWouldOverlap, groupDrag, setGroupDrag, dragOverlay, setDragOverlay, isJustAdded,
  type GroupDrag,
  editOwnsGesture, grabbedItemId, setGrabbedItemId,
  moveArmedItemId, pickButtonForDisplayId, cancelPickButton,
} from '../layout/store.ts';
import { midiAllocFailure } from '../layout/midiAlloc.ts';
import { itemMenu, openItemMenu } from '../layout/itemMenu.ts';
import { editTriggersEnabled, EDIT_GRAB_HOVER_MS, snapEnabled, controlGap } from '../settings/store.ts';
import { hueOf, isUnassigned, HUE_VALUES, type PaletteHue } from '../layout/projectStore.ts';
import { isSquareKind } from '../layout/sizes.ts';
import { itemResizeClamps } from '../layout/store.ts';
import { applyAspectRule } from '../layout/shapes.ts';
import { timelineLit, timelineFast } from '../timeline/canvasLights.ts';
import { countInProgress } from '../timeline/countIn.ts';
import {
  boundsOf, snapBox, snapEdge, nearestFreeShift, SNAP_SCREEN_PX, type Guide, type Rect,
} from '../layout/placement.ts';

/** 8 resize handle directions. Sides drive one axis; corners drive both.
 *  Direction encodes which edges move — 'n' means the top edge follows the
 *  cursor (h shrinks, y grows), 'e' means the right edge follows (w changes,
 *  x fixed), etc. */
type ResizeDir = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

const RESIZE_DIRS: ResizeDir[] = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];

/** Every touch pointer that is down on a control, and the ZONE it landed in
 *  (see `guardPointerDown`). Module-level: a finger is in one place at a time,
 *  and one lifted anywhere must clear its own entry, so a single window
 *  listener owns the release. */
const touchZones = new Map<number, Element>();
if (typeof window !== 'undefined') {
  const release = (ev: PointerEvent) => { touchZones.delete(ev.pointerId); };
  window.addEventListener('pointerup', release, true);
  window.addEventListener('pointercancel', release, true);
  window.addEventListener('blur', () => touchZones.clear());
}

/** Kinds whose component already gives every finger its own note or strip (a
 *  pointer-id map inside it): the piano's chords, the mixer's channel faders.
 *  The wrapper leaves their fingers alone entirely. */
const OWN_MULTITOUCH_KINDS: ReadonlySet<string> = new Set(['piano', 'mixer']);


/** How long a dropped control takes to ease into its final spot. Matches the
 *  `translate` transition on `.editable-item[data-settling]` in app.css. */
const SETTLE_MS = 180;

/**
 * Commit a drop and ease the set from where it was let go (`from`, relative to
 * where it started) into where it ends up (`to`). The store takes the final
 * spot at once, in the same batch as an offset that keeps the set drawn
 * exactly where it was let go; a frame later the offset goes to zero with the
 * transition on, so the set glides in. A snap-back is the same glide home.
 * Nothing persisted is ever in between.
 */
function settleInto(
  ids: string[], from: { dx: number; dy: number }, to: { dx: number; dy: number }, commit: () => void,
): void {
  const offX = from.dx - to.dx;
  const offY = from.dy - to.dy;
  const still = Math.abs(offX) < 0.5 && Math.abs(offY) < 0.5;
  const held: GroupDrag | null = still ? null : { ids, dx: offX, dy: offY };
  batch(() => {
    commit();
    setGroupDrag(held);
  });
  if (!held) return;
  // Two frames: the first paints the held offset, so the change to zero in
  // the second is one the transition can animate.
  requestAnimationFrame(() => requestAnimationFrame(() => {
    if (groupDrag() !== held) return; // a new drag took over
    const settle: GroupDrag = { ids, dx: 0, dy: 0, settling: true };
    setGroupDrag(settle);
    setTimeout(() => { if (groupDrag() === settle) setGroupDrag(null); }, SETTLE_MS + 60);
  }));
}

/** Pixels of pointer movement that distinguish a click from a drag.
 *  Lower for mouse (precision), higher for touch (finger jitter). */
const CLICK_THRESHOLD_MOUSE = 4;
const CLICK_THRESHOLD_TOUCH = 10;

interface EditableItemProps {
  item: LayoutItem;
  children: JSX.Element;
}

export function EditableItem(props: EditableItemProps) {
  let wrapperRef: HTMLDivElement | undefined;
  const [dragging, setDragging] = createSignal(false);
  const [resizing, setResizing] = createSignal(false);
  const isSelected = () => isItemSelected(props.item.id);
  /** True when this item moves as part of a group rather than on its own. */
  const inMultiSelection = () => isSelected() && selectedItemIds().length > 1;
  const grabbed = () => grabbedItemId() === props.item.id;
  /** Armed for a drag by the context menu's Move row. Distinct from
   *  `grabbed()` (a hover dwell) so the two affordances can look different
   *  and so a mouse-out can't cancel an explicit choice. */
  const moveArmed = () => moveArmedItemId() === props.item.id;
  /** True while THIS item is the one the open context menu points at
   *  (2026-08-09). Right-click deliberately no longer selects, so without a
   *  mark of its own the gesture left NOTHING on the canvas: on a dense page
   *  the menu opens over a neighbour, and "which one am I about to delete?"
   *  was answerable only by reading the menu's header. The store signal is the
   *  authority rather than a local flag, which is what guarantees exactly one
   *  item can be lit at a time. */
  const menuOpen = () => itemMenu()?.itemId === props.item.id;

  // Hover-grab (edit mode, triggers-on): a ~2s mouse hover over the item
  // "grabs" it — hand cursor, its trigger is suppressed (editOwnsGesture),
  // and it becomes draggable. Moving off ungrabs it (unless mid-drag).
  let hoverTimer: number | undefined;
  function clearHoverTimer() {
    if (hoverTimer !== undefined) { clearTimeout(hoverTimer); hoverTimer = undefined; }
  }
  function onWrapperPointerEnter(ev: PointerEvent) {
    if (ev.pointerType !== 'mouse') return; // hover-dwell is a mouse gesture
    if (!isEditMode() || !editTriggersEnabled() || grabbed()) return;
    clearHoverTimer();
    hoverTimer = window.setTimeout(() => {
      hoverTimer = undefined;
      setGrabbedItemId(props.item.id);
    }, EDIT_GRAB_HOVER_MS);
  }
  function onWrapperPointerLeave() {
    clearHoverTimer();
    if (grabbed() && !dragging()) setGrabbedItemId(null);
  }
  onCleanup(clearHoverTimer);

  // Live position during drag. Committed to the store only on drop, so
  // the rest of the app (which reads from the store) sees the final
  // resting place, not intermediate frames.
  const [liveX, setLiveX] = createSignal(props.item.x);
  const [liveY, setLiveY] = createSignal(props.item.y);
  // Live dimensions during resize. Same commit-on-drop model. Resize
  // from N or W edges also shifts x/y, so live position tracks alongside.
  const [liveW, setLiveW] = createSignal(props.item.w);
  const [liveH, setLiveH] = createSignal(props.item.h);

  // A NON-PRIMARY PRESS NEVER REACHES THE CONTROL (2026-08-09, widened
  // 2026-08-11). Operator: *"when right clicking on an item, don't change
  // the value (no action)"*. `pointerdown` fires for the right button
  // exactly as it does for the left, so a right-click on a fader ran its
  // whole drag path - it jumped the handle to the cursor, sent the CC, and
  // persisted the value, all before the context menu had even opened. Every
  // continuous control (fader, radial, XY pad) had the same hole, and a
  // button fired too.
  //
  // WIDENED 2026-08-11 (.notepads/edit-play-master-toggle.md item 3) to also
  // cover a second finger. Buttons fire the instant a finger touches down
  // (deliberate, zero-latency), so a careless two-finger tap on a control
  // could register as two separate `pointerdown`s and fire it twice - even
  // with the item context menu now gated to edit-mode-only (App.tsx's
  // `onCanvasTouchEnd`), because "triggers in edit mode" (DEC-090) can still
  // leave a control live-firing while editing. `PointerEvent.isPrimary` is
  // false for every touch point after the first in a multi-touch gesture,
  // and always true for a mouse (which has only one pointer, whichever
  // button is down) - so it can't replace the button!==0 check above, only
  // extend the same rule to a second finger. One rule, "any non-primary
  // press never reaches the control," covers both a right-click and a
  // second finger instead of two separate rules.
  //
  // Fixed once, here on the wrapper, rather than in each control's own
  // handler: the wrapper is the one element every kind is guaranteed to have,
  // so a kind added later is covered without knowing about this rule.
  //
  // CAPTURE PHASE + stopPropagation is what makes it work. Solid delegates
  // `pointerdown` at the document root, so a bubble-phase handler here runs
  // AFTER the child's - too late. Stopping it on the way DOWN means neither
  // the child nor the delegated root listener ever sees it.
  //
  // NARROWED FOR PLAY 2026-09-30 (next-update-plan 1.4). "Any second finger"
  // was the whole SURFACE, not the one control: holding fader A, a finger
  // dragging fader B sent nothing (measured: B alone sent 6 CCs, B with A held
  // sent 0) - no two faders at once, no pads together, no tapping a button
  // while riding a fader. The operator's rule: every control is independent,
  // while two fingers on the SAME button still count once. So, outside edit
  // mode, a finger is swallowed only when another finger is already down in
  // the same ZONE: the nearest `<button>` (a button-grid cell, a sequencer
  // step, an OPVisual cell, a FireButton) or `[data-touch-zone]` it landed
  // on, else the whole item (a fader, a radial, an XY pad). Kinds that give
  // every finger its own note or strip themselves (piano, mixer) are left
  // alone. EDIT mode keeps the old one-finger rule, so a two-finger gesture
  // stays a gesture and never starts a drag or a fire.
  function guardPointerDown(ev: PointerEvent) {
    if (ev.pointerType === 'mouse') {
      if (ev.button === 0) return;
      ev.stopPropagation();
      return;
    }
    if (isEditMode()) {
      if (ev.isPrimary) return;
      ev.stopPropagation();
      return;
    }
    if (OWN_MULTITOUCH_KINDS.has(props.item.kind) || !wrapperRef) return;
    const hit = ev.target instanceof Element ? ev.target.closest('button, [data-touch-zone]') : null;
    const zone = hit && wrapperRef.contains(hit) ? hit : wrapperRef;
    for (const z of touchZones.values()) {
      if (z === zone) { ev.stopPropagation(); return; }
    }
    touchZones.set(ev.pointerId, zone);
  }
  onMount(() => wrapperRef?.addEventListener('pointerdown', guardPointerDown, true));
  onCleanup(() => wrapperRef?.removeEventListener('pointerdown', guardPointerDown, true));

  /**
   * THE INSPECTOR FOLLOWS THE POINTER (2026-08-09). Operator: *"if inspector
   * is open and user left or right clicks another item, keep inspector open
   * and change the inspector details to the newly clicked item."*
   *
   * Gated on something ALREADY being selected, and that is the whole design:
   * with the inspector shut, a click still means whatever it meant before
   * (fire the control in test mode, select in edit mode) and a right-click
   * still does not summon the app's heaviest surface - which is the ask this
   * menu was built for in the first place. With the inspector open the
   * operator is plainly in a tweaking posture, and re-pointing it is cheaper
   * than close-then-reopen on the other item.
   */
  function followSelectionTo(): void {
    if (selectedItemId() === null || isSelected()) return;
    selectItem(props.item.id);
  }

  /**
   * WHAT A PLAIN (unmodified) PRESS DOES TO THE SELECTION.
   *
   * One rule reconciles two asks made hours apart that both land on this
   * gesture - *"if inspector is open and user left or right clicks another
   * item, change the inspector to the newly clicked item"* and, on the iPad
   * where there is no ctrl key, *"tap toggles"*:
   *
   *   0 or 1 highlighted -> REPLACE. Exactly today's behaviour: the item
   *     becomes the selection and the inspector follows it.
   *   2+ highlighted     -> TOGGLE. You are visibly in a multi-selection, so
   *     a tap refines it - which is what makes a marquee result fixable by
   *     hand on a device with no modifier key.
   *
   * Making a tap ALWAYS toggle was the other option and is worse: the
   * single-item edit is the 95% case, and it would have cost a second tap to
   * de-select the previous item every time, while silently undoing the
   * inspector-follows-click behaviour shipped the same day.
   */
  function plainPressSelects(): void {
    if (selectedItemIds().length > 1) toggleItemSelected(props.item.id);
    else selectItem(props.item.id);
  }

  // Right-click → open the item context menu. EDIT MODE ONLY (2026-08-11,
  // operator: "edit only, yes" - closing the same race the master Edit/Test
  // toggle exists for, on the mouse path too. Previously Test mode opened
  // the menu same as Edit mode; now `!isEditMode()` covers Play AND Show in
  // one check - Performance mode's own reasoning is unchanged (SD-2, editing fully
  // locked out).
  //
  // CONSEQUENCE, surfaced not buried: this was the ONLY path to selecting an
  // item while starting from nothing selected in Test mode (via the menu's
  // "Inspect" row) - ctrl+click-select is edit-mode-only (see
  // onWrapperPointerDown below) and a plain test-mode tap fires the control
  // instead of selecting it. DEC-054 ("right-click in test mode grants
  // drag-anywhere") is therefore unreachable in practice now, not just
  // narrowed - there is no remaining on-ramp into it. Flagged for the
  // operator to confirm DEC-054 should be retired/adapted; not decided here.
  //
  // PreventDefault to suppress the OS context menu; stopPropagation so the
  // canvas menu (page-level actions) doesn't also open behind it. Both are
  // skipped on the early return - the event bubbles to the canvas handler
  // (also edit-mode-gated as of the same change) and then App's app-wide
  // suppressor, either of which kills the native menu without opening
  // anything, same pattern Performance mode already used before this change.
  function onWrapperContextMenu(ev: MouseEvent) {
    if (!isEditMode()) return;
    ev.preventDefault();
    ev.stopPropagation();
    // WHAT A RIGHT-CLICK DOES TO THE SELECTION. Two cases remain now that
    // this only ever runs in edit mode:
    //
    //   * on an item already highlighted -> nothing. The group survives, which
    //     is the entire point of "right click any of the highlighted ones".
    //   * otherwise -> collapse the highlight onto this item, quietly.
    //     Collapsing is the Finder/Explorer convention and it lets the menu
    //     treat "what do I act on" as one idea instead of a union of "the
    //     selection" and "the thing under the cursor"; quietly, because
    //     *"right click shouldn't auto bring up the inspector"*.
    if (isSelected()) { /* leave the group as it is */ }
    else if (inspectorItem() !== null) selectItem(props.item.id);
    else highlightItem(props.item.id);
    openItemMenu(props.item.id, ev.clientX, ev.clientY);
  }

  // Click-vs-drag arbiter. Pointer-down sets up listeners; whichever
  // happens first decides the gesture:
  //   move past threshold → drag (existing move logic).
  //   pointer-up before threshold → click (select this item).
  //
  // Runs in edit mode, and in test mode for either of two escape hatches:
  //   * the item is armed by the context menu's Move row (2026-08-09) —
  //     covered by `editOwnsGesture`, which returns true for an armed item
  //     in any mode;
  //   * the item is the currently-selected one (DEC-054). Right-click no
  //     longer selects, so this now means "Inspect-ed via the menu, then
  //     dragged" — the inspector is open on it, so a drag is unambiguous.
  // Deselecting (Esc / click canvas) or dropping an armed move turns the
  // drag gate back off, so test-mode taps on other items still fire.
  function onWrapperPointerDown(ev: PointerEvent) {
    // Pick-a-button mode (DisplayItem source=button "click on canvas",
    // 2026-08-12): a click on a Button item resolves the pick and binds it
    // to the DisplayItem that started picking - nothing else about this
    // item's normal select/drag logic runs. Non-button items sit behind
    // `pointer-events: none` while picking (app.css
    // `.shell[data-pick-mode]`), so this branch only reaches the button
    // case in practice; the explicit kind check is defensive, not
    // load-bearing. Checked before ctrl+click / ownership so picking wins
    // over every other interpretation of the same press.
    const pickingFor = pickButtonForDisplayId();
    if (pickingFor) {
      if (props.item.kind === 'button') {
        ev.preventDefault();
        ev.stopPropagation();
        editItem(pickingFor, { buttonId: props.item.id });
        cancelPickButton();
      }
      return;
    }
    // CTRL+CLICK = toggle this item in or out of the highlight (2026-08-09).
    // Handled here and returned, before the ownership gate and before any
    // drag setup: ctrl+click is a selection gesture, never the start of a
    // drag, and letting it fall through would arm a drag the operator did
    // not ask for.
    //
    // EDIT MODE ONLY, and deliberately so. In test mode a press fires a
    // control on a rig that gets gigged; a modifier that silently accumulates
    // a selection there is a way to arrive at "delete" holding five things
    // you did not mean to pick. Test mode keeps exactly the single-select
    // behaviour it has always had.
    if (isEditMode() && ev.pointerType === 'mouse' && ev.button === 0 && (ev.ctrlKey || ev.metaKey)) {
      ev.preventDefault();
      ev.stopPropagation();
      toggleItemSelected(props.item.id);
      return;
    }
    // Re-point an already-open inspector before the ownership gate below,
    // which returns early for a test-mode tap on an unselected item - the
    // exact case the operator asked about. Deliberately on pointer-DOWN and
    // without preventing the event: the control still fires (play) and the
    // drag arbiter still runs (edit), the inspector just follows along.
    followSelectionTo();
    // The wrapper owns the tap (select / drag) only when edit-mode gestures
    // apply to this item (editOwnsGesture) OR a selected item in test mode
    // (DEC-054). Otherwise the tap belongs to the child control — with
    // "triggers in edit mode" on, an un-grabbed item fires instead of moving.
    const playSelectedDrag = !isEditMode() && isSelected();
    if (!editOwnsGesture(props.item.id) && !playSelectedDrag) return;
    // Ignore presses that land on the resize hit zones — each zone owns
    // its own pointer-down handler.
    const src = ev.target as HTMLElement | null;
    if (src && src.closest('.editable-resize-zone')) return;
    // Only primary button (left mouse / touch / pen).
    if (ev.button !== 0 && ev.pointerType === 'mouse') return;
    ev.preventDefault();
    ev.stopPropagation();

    const startX = ev.clientX;
    const startY = ev.clientY;
    const originX = props.item.x;
    const originY = props.item.y;
    const threshold = ev.pointerType === 'mouse'
      ? CLICK_THRESHOLD_MOUSE
      : CLICK_THRESHOLD_TOUCH;
    const target = ev.currentTarget as HTMLElement;
    target.setPointerCapture(ev.pointerId);

    // Drag math anchor (computed eagerly so the handler can use it as
    // soon as the threshold trips, without a second DOM read).
    const canvasEl = target.closest('.scaled-canvas-inner') as HTMLElement | null;
    let scale = 1;
    if (canvasEl) {
      const rect = canvasEl.getBoundingClientRect();
      if (canvasEl.clientWidth > 0) scale = rect.width / canvasEl.clientWidth;
    }

    // THE MOVING SET. A multi-selection drags as one; anything else is a set
    // of exactly one, and every line below then reduces to the single-item
    // behaviour this has always had. One code path rather than two is what
    // keeps the group case from quietly diverging on clamps or snap-back.
    const onPage = layoutItems();
    const groupItems = (inMultiSelection()
      ? selectedItemIds()
          .map((id) => onPage.find((it) => it.id === id))
          .filter((it): it is LayoutItem => it !== undefined)
      : [props.item]);
    const groupIds = new Set(groupItems.map((it) => it.id));
    const allIds = groupItems.map((it) => it.id);
    const origins = groupItems.map((it) => ({ id: it.id, x: it.x, y: it.y, w: it.w, h: it.h }));
    // Everything that is not moving: what the set snaps to, and what a drop
    // must not land on.
    const obstacles: Rect[] = onPage.filter((it) => !groupIds.has(it.id));

    // Clamp the DELTA against the group's bounding box, not each item
    // separately: clamping per item would let the leading edge stop while the
    // rest kept going, shearing a set that is supposed to move rigidly.
    const canvasW = canvasEl ? canvasEl.clientWidth : Infinity;
    const canvasH = canvasEl ? canvasEl.clientHeight : Infinity;
    const box0 = boundsOf(origins);
    const dxMin = -box0.x;
    const dxMax = canvasW - (box0.x + box0.w);
    const dyMin = -box0.y;
    const dyMax = canvasH - (box0.y + box0.h);
    const measured = Number.isFinite(canvasW) && Number.isFinite(canvasH);
    const bounds = { w: canvasW, h: canvasH };
    // Same feel at any zoom: the snap distance is screen px.
    const snapThreshold = SNAP_SCREEN_PX / scale;

    // If the set already overlaps something outside it at pickup time, skip
    // the drop-time overlap check - otherwise any micro-move snaps right back
    // because the pre-existing overlap is still there. Lets the user
    // disentangle stacked items by dragging them apart.
    const originOverlapped = groupWouldOverlap(origins, groupIds);

    const placed = (dx: number, dy: number) => origins.map((o) => ({ ...o, x: o.x + dx, y: o.y + dy }));

    let didStartDrag = false;
    /** Where the set is drawn right now, relative to where it started. */
    let cur = { dx: 0, dy: 0 };
    let curGuides: Guide[] = [];
    let blocked = false;
    /** Where a blocked drop would settle, relative to `cur` (null = it has
     *  nowhere near to go and would go back). Worked out at most once a frame. */
    let settleShift: { dx: number; dy: number } | null = null;
    let settleFrame = 0;

    /** Delta in design px: snapped (unless Alt, or snapping is off), then
     *  clamped to keep the whole set on canvas. */
    function deltaFrom(e: PointerEvent): { dx: number; dy: number; guides: Guide[] } {
      let dx = (e.clientX - startX) / scale;
      let dy = (e.clientY - startY) / scale;
      let guides: Guide[] = [];
      if (measured && snapEnabled() && !e.altKey) {
        const s = snapBox({ x: box0.x + dx, y: box0.y + dy, w: box0.w, h: box0.h }, obstacles, bounds, snapThreshold, controlGap());
        dx += s.sx;
        dy += s.sy;
        guides = s.guides;
      }
      const cx = Math.max(dxMin, Math.min(dxMax, dx));
      const cy = Math.max(dyMin, Math.min(dyMax, dy));
      // A guide the clamp pulled the set off of no longer tells the truth.
      if (cx !== dx) guides = guides.filter((g) => g.axis !== 'x' || g.gap);
      if (cy !== dy) guides = guides.filter((g) => g.axis !== 'y' || g.gap);
      return { dx: cx, dy: cy, guides };
    }

    function showOverlay(): void {
      const ghosts = blocked && settleShift
        ? placed(cur.dx + settleShift.dx, cur.dy + settleShift.dy).map(({ x, y, w, h }) => ({ x, y, w, h }))
        : [];
      setDragOverlay({ guides: curGuides, ghosts, blocked });
    }

    const onMove = (e: PointerEvent) => {
      const dxScreen = e.clientX - startX;
      const dyScreen = e.clientY - startY;
      if (!didStartDrag) {
        if (Math.abs(dxScreen) <= threshold && Math.abs(dyScreen) <= threshold) return;
        didStartDrag = true;
        setDragging(true);
      }
      const { dx, dy, guides } = deltaFrom(e);
      const movedSpot = dx !== cur.dx || dy !== cur.dy;
      cur = { dx, dy };
      curGuides = guides;
      // Every member draws through this offset, the item under the pointer
      // included. Store-level view state, never a per-frame item write.
      setGroupDrag({ ids: allIds, dx, dy });
      blocked = !originOverlapped && groupWouldOverlap(placed(dx, dy), groupIds);
      if (!blocked) settleShift = null;
      showOverlay();
      // Where a blocked drop would settle: searched at most once a frame,
      // because the search is the one per-move cost here that grows with the
      // page. The previous answer keeps showing until the new one is in.
      if (!blocked || !measured || !movedSpot || settleFrame) return;
      settleFrame = requestAnimationFrame(() => {
        settleFrame = 0;
        if (!blocked) return;
        settleShift = nearestFreeShift(placed(cur.dx, cur.dy), obstacles, bounds, undefined, controlGap());
        showOverlay();
      });
    };
    const onUp = () => {
      target.releasePointerCapture(ev.pointerId);
      target.removeEventListener('pointermove', onMove);
      target.removeEventListener('pointerup', onUp);
      target.removeEventListener('pointercancel', onUp);
      if (settleFrame) { cancelAnimationFrame(settleFrame); settleFrame = 0; }
      if (!didStartDrag) {
        // Click, no drag. In edit mode that selects the item for the
        // inspector. On an ARMED item it is now a no-op (2026-08-09): the arm
        // survives until the operator presses somewhere else, so a tap on the
        // item itself must not cancel it - that would make the gesture meaning
        // "still working on this one" identical to the one meaning "never
        // mind". Selecting is equally wrong here: popping the inspector is the
        // very thing the menu exists to avoid.
        if (moveArmed()) return;
        plainPressSelects();
        return;
      }
      // Where the set ends up. A drop on another control SETTLES into the
      // nearest free spot (2026-10-10) - the ghost outline showed where while
      // dragging - instead of jumping back to where it started, which read as
      // the app refusing the move. Only a drop with no room near it goes
      // back. The whole set moves or none of it does: landing three of five
      // would break the arrangement the operator was moving.
      let final = cur;
      if (blocked) {
        const s = measured ? nearestFreeShift(placed(cur.dx, cur.dy), obstacles, bounds, undefined, controlGap()) : null;
        final = s ? { dx: cur.dx + s.dx, dy: cur.dy + s.dy } : { dx: 0, dy: 0 };
      }
      settleInto(allIds, cur, final, () => {
        if (final.dx !== 0 || final.dy !== 0) {
          moveItems(new Map(placed(final.dx, final.dy).map((p) => [p.id, { x: p.x, y: p.y }])));
        }
        setDragOverlay(null);
        setDragging(false);
      });
      // ARM SURVIVES THE DROP (2026-08-09). Operator: *"when moving item, keep
      // it movable until user clicks off the item."* It used to be one arm,
      // one move - which meant nudging something into place cost a trip back
      // to the right-click menu for every single nudge, and placing an item is
      // almost never one drag.
      //
      // The old comment warned that leaving it armed keeps the item's trigger
      // suppressed and "reads as a dead control" in test mode. That risk is
      // real and is answered rather than ignored: an armed item wears the
      // pulsing amber dashed outline the whole time, so the state is never
      // silent, and `disarmOnPressOutside` (App.tsx) ends it the moment the
      // operator presses anything else - which is the next thing they do.
    };
    target.addEventListener('pointermove', onMove);
    target.addEventListener('pointerup', onUp);
    target.addEventListener('pointercancel', onUp);
  }

  // Resize handler — one of 8 edge/corner hit zones. Direction encodes
  // which edges follow the cursor. Two independent behaviours:
  //
  //   Sides (n/s/e/w) — drive a single axis, always. Dragging E moves
  //     the right edge and only the right edge; h stays at origin.
  //     Shape identity yields: dragging E on a circle produces an
  //     ellipse for the duration of the drag. This matches every
  //     standard editor (Figma, browser inspector, …). Shape aspect
  //     rules do NOT apply to sides — the operator picked this handle
  //     specifically because they wanted single-axis intent.
  //   Corners (ne/nw/se/sw) — dimension-locked by default: the current
  //     w:h ratio is preserved and the corner scales the whole shape.
  //     Circles stay circles, squares stay square, etc. via the
  //     origin-aspect projection.
  //
  //   Shift held — breaks the dimension lock on corners:
  //     * Corner + Shift: axes decouple. Cursor dx drives w, dy drives h
  //       independently. Standard "free resize" mode.
  //     * Sides are unchanged by Shift (they're already single-axis).
  //
  // For N and W handles the item's x/y also shift: pulling the top
  // edge down increases y and decreases h; pulling the left edge right
  // increases x and decreases w. The commit path uses moveAndResizeItem
  // for those directions so the frame updates atomically.
  function makeResizePointerDown(dir: ResizeDir) {
    return (ev: PointerEvent) => {
      // buttongrid + mixer derive their pixel size from a count × cell, so
      // freeform resize would distort them — they resize via the S/M/L preset.
      if (props.item.kind === 'buttongrid' || props.item.kind === 'mixer') return;
      ev.preventDefault();
      ev.stopPropagation();
      const startClientX = ev.clientX;
      const startClientY = ev.clientY;
      const originX = props.item.x;
      const originY = props.item.y;
      const originW = props.item.w;
      const originH = props.item.h;
      setLiveX(originX);
      setLiveY(originY);
      setLiveW(originW);
      setLiveH(originH);
      setResizing(true);
      const target = ev.currentTarget as HTMLElement;
      target.setPointerCapture(ev.pointerId);

      const kind = props.item.kind;
      const isButton = kind === 'button';
      const buttonShape = kind === 'button' ? props.item.shape ?? 'rect' : null;
      const { minW, minH, maxW, maxH } = itemResizeClamps(props.item);
      const legacySquare = !isButton && isSquareKind(kind);
      const canvasEl = target.closest('.scaled-canvas-inner') as HTMLElement | null;
      const canvasW = canvasEl ? canvasEl.clientWidth : Infinity;
      const canvasH = canvasEl ? canvasEl.clientHeight : Infinity;
      let scale = 1;
      if (canvasEl) {
        const rect = canvasEl.getBoundingClientRect();
        if (canvasEl.clientWidth > 0) scale = rect.width / canvasEl.clientWidth;
      }

      // Per-direction axis flags: which edges follow the cursor.
      const affectN = dir === 'n' || dir === 'ne' || dir === 'nw';
      const affectS = dir === 's' || dir === 'se' || dir === 'sw';
      const affectE = dir === 'e' || dir === 'ne' || dir === 'se';
      const affectW = dir === 'w' || dir === 'nw' || dir === 'sw';
      const isCorner = (affectN || affectS) && (affectE || affectW);
      const originAspect = originH === 0 ? 1 : originW / originH;
      // What the moving edges snap to (2026-10-10): every other control's
      // edges, the standard gap beside a level one, the canvas edges, else
      // the grid. Same rules and distance as a drag.
      const others: Rect[] = layoutItems().filter((it) => it.id !== props.item.id);
      const measured = Number.isFinite(canvasW) && Number.isFinite(canvasH);
      const bounds = { w: canvasW, h: canvasH };
      const snapThreshold = SNAP_SCREEN_PX / scale;

      const onMove = (e: PointerEvent) => {
        const shiftHeld = e.shiftKey;
        // Ratio lock: session toggle XOR Shift. Locked = uniform scale
        // (sides + corners preserve origin aspect). Shift temporarily
        // inverts the lock, matching Figma muscle memory (Shift releases
        // when the lock is on; Shift adds nothing when the lock is off).
        const uniformScale = isRatioLocked() !== shiftHeld;
        let dxDesign = (e.clientX - startClientX) / scale;
        let dyDesign = (e.clientY - startClientY) / scale;
        const guides: Guide[] = [];
        if (measured && snapEnabled() && !e.altKey) {
          const alongX: [number, number] = [originY, originY + originH];
          const alongY: [number, number] = [originX, originX + originW];
          if (affectE) {
            const r = snapEdge('x', originX + originW + dxDesign, 'hi', alongX, others, bounds, snapThreshold, controlGap());
            dxDesign = r.value - (originX + originW); guides.push(...r.guides);
          } else if (affectW) {
            const r = snapEdge('x', originX + dxDesign, 'lo', alongX, others, bounds, snapThreshold, controlGap());
            dxDesign = r.value - originX; guides.push(...r.guides);
          }
          if (affectS) {
            const r = snapEdge('y', originY + originH + dyDesign, 'hi', alongY, others, bounds, snapThreshold, controlGap());
            dyDesign = r.value - (originY + originH); guides.push(...r.guides);
          } else if (affectN) {
            const r = snapEdge('y', originY + dyDesign, 'lo', alongY, others, bounds, snapThreshold, controlGap());
            dyDesign = r.value - originY; guides.push(...r.guides);
          }
        }

        // Anchor stays put on the opposite edge. Compute the new (x, y,
        // w, h) frame from the cursor delta + direction.
        let nx = originX;
        let ny = originY;
        let nw = originW;
        let nh = originH;
        if (affectE) nw = originW + dxDesign;
        if (affectW) { nx = originX + dxDesign; nw = originW - dxDesign; }
        if (affectS) nh = originH + dyDesign;
        if (affectN) { ny = originY + dyDesign; nh = originH - dyDesign; }

        // Uniform scale: preserve origin aspect ratio. Applies to both
        // sides and corners when engaged. Which axis drives depends on
        // direction: sides drive their own axis; corners pick the axis
        // with the larger signed delta.
        if (uniformScale) {
          if (isCorner) {
            const wDelta = affectE ? dxDesign : (affectW ? -dxDesign : 0);
            const hDelta = affectS ? dyDesign : (affectN ? -dyDesign : 0);
            if (Math.abs(wDelta) >= Math.abs(hDelta)) {
              nh = originW === 0 ? originH : (nw / originAspect);
            } else {
              nw = originH === 0 ? originW : (nh * originAspect);
            }
          } else if (affectE || affectW) {
            // Side driving W: derive H from originAspect.
            nh = originAspect === 0 ? originH : (nw / originAspect);
          } else {
            // Side driving H: derive W from originAspect.
            nw = nh * originAspect;
          }
        }

        // Legacy square-lock (streamdeck) — always both axes together,
        // regardless of Shift. Its identity is squareness.
        if (legacySquare) {
          const dominant: 'w' | 'h' = Math.abs(nw - originW) >= Math.abs(nh - originH) ? 'w' : 'h';
          const both = dominant === 'w' ? nw : nh;
          nw = both;
          nh = both;
        }

        // Clamp size to [min, max] and to canvas bounds.
        const maxWBound = affectW
          ? originX + originW
          : Math.min(maxW, canvasW - originX);
        const maxHBound = affectN
          ? originY + originH
          : Math.min(maxH, canvasH - originY);
        nw = Math.max(minW, Math.min(maxW, Math.min(maxWBound, nw)));
        nh = Math.max(minH, Math.min(maxH, Math.min(maxHBound, nh)));

        // Shape aspect rule — buttons only, and only when the operator
        // is in uniform-scale mode. Sides without uniform-scale bypass
        // the rule (single-axis intent — circle can go elliptical for
        // this drag). Uniform-scale corners already preserve aspect via
        // originAspect; the rule reconciles when originAspect drifted
        // off-canon (e.g. rect stretched non-uniformly earlier).
        if (isButton && buttonShape && uniformScale) {
          const dominantAxis: 'w' | 'h' =
            isCorner ? (Math.abs(nw - originW) >= Math.abs(nh - originH) ? 'w' : 'h')
            : (affectE || affectW) ? 'w'
            : 'h';
          const adjusted = applyAspectRule(buttonShape, nw, nh, dominantAxis);
          nw = Math.max(minW, Math.min(maxW, adjusted.w));
          nh = Math.max(minH, Math.min(maxH, adjusted.h));
        }

        // Re-anchor x/y after clamps: N/W handles must pin their opposite
        // edge exactly at (originX+originW) or (originY+originH).
        if (affectW) nx = originX + originW - nw;
        if (affectN) ny = originY + originH - nh;
        // Non-negative safety.
        if (nx < 0) { nw = nw + nx; nx = 0; }
        if (ny < 0) { nh = nh + ny; ny = 0; }

        setLiveX(nx);
        setLiveY(ny);
        setLiveW(nw);
        setLiveH(nh);
        // Red while the new size would cover another control: a resize that
        // overlaps still goes back on release, so say so before it does.
        // Guides only for an edge that ended up where it snapped (a clamp or
        // an aspect rule can pull it off).
        const edges = [nx, nx + nw, ny, ny + nh];
        setDragOverlay({
          guides: guides.filter((g) => edges.some((v) => Math.abs(v - g.pos) < 0.5)),
          ghosts: [],
          blocked: wouldOverlap(nx, ny, nw, nh, props.item.id),
        });
      };
      const onUp = () => {
        target.releasePointerCapture(ev.pointerId);
        target.removeEventListener('pointermove', onMove);
        target.removeEventListener('pointerup', onUp);
        target.removeEventListener('pointercancel', onUp);
        const nx = liveX();
        const ny = liveY();
        const nw = liveW();
        const nh = liveH();
        setDragOverlay(null);
        if (wouldOverlap(nx, ny, nw, nh, props.item.id)) {
          setLiveX(originX);
          setLiveY(originY);
          setLiveW(originW);
          setLiveH(originH);
        } else if (nx !== originX || ny !== originY) {
          moveAndResizeItem(props.item.id, nx, ny, nw, nh);
        } else {
          resizeItem(props.item.id, nw, nh);
        }
        setResizing(false);
      };
      target.addEventListener('pointermove', onMove);
      target.addEventListener('pointerup', onUp);
      target.addEventListener('pointercancel', onUp);
    };
  }

  /** This item's share of a drag in flight, or of the settle after one: the
   *  offset it is drawn at from its stored spot. Every member of the moving
   *  set reads it, the item under the pointer included (2026-10-10). */
  // A memo: every control reads the drag, but only the moving ones may
  // react to each pointer move. Unmemoised, every control on the page re-ran
  // its style and attribute bindings on every move of any drag.
  const dragShift = createMemo(() => {
    const g = groupDrag();
    return g && g.ids.includes(props.item.id) ? g : null;
  });
  /** Moving with the set but not under the pointer: a FOLLOWER. */
  const following = () => !dragging() && !!dragShift() && !dragShift()!.settling;
  /** Part of a drag whose drop would land on another control. */
  const blockedNow = createMemo(() => (dragging() || following() || resizing()) && dragOverlay()?.blocked === true);

  // Read positions reactively - when the store changes (e.g. after a
  // successful move) the underlying signal updates and we re-render.
  // During resize, x/y track live (N/W handles shift the anchor); a drag
  // leaves them alone and moves the item by `translate` instead.
  const x = () => (resizing() ? liveX() : props.item.x);
  const y = () => (resizing() ? liveY() : props.item.y);
  const w = () => (resizing() ? liveW() : props.item.w);
  const h = () => (resizing() ? liveH() : props.item.h);

  // Buttons carry shape + rotation. Rotation applies to the wrapper as a
  // CSS transform so visuals + hit-area rotate together. Shape drives
  // clip-path on FireButton; data-shape here lets CSS address per-shape
  // wrapper affordances if we ever want to.
  const isButton = () => props.item.kind === 'button';
  const buttonShape = () => (isButton() ? (props.item as { shape?: string }).shape ?? 'rect' : null);
  const buttonRotation = () => (isButton() ? (props.item as { rotation?: number }).rotation ?? 0 : 0);
  /** Accessor, not a captured const - reading props.item inside the body
   *  would re-create this subtree on every edit and blur a live input
   *  (DEC-119/120). */
  const itemHue = () => hueOf(props.item as { hue?: PaletteHue; color?: string });
  /** Soft red pulse for a control that points at nothing (2026-07-30).
   *  Read here, on the one element every kind is guaranteed to have, so the
   *  pulse is a single CSS rule that also covers kinds added later - the
   *  per-kind logic lives in `isUnassigned`, not in each component. Accessor
   *  for the DEC-119/120 reason above. */
  const unassigned = () => isUnassigned(props.item);
  /** A copy that could not be given a free CC / note on its channel
   *  (2026-08-09). Rides the SAME red pulse as `unassigned` - both mean "this
   *  control will not do what you think", and inventing a second colour for
   *  the second flavour of that would make neither read. The inspector is
   *  where they differ: it says which one it is, and what to do. */
  const midiClash = () => midiAllocFailure(props.item.id) !== null;

  /** True while a "click on canvas" button pick (DisplayItem source=button)
   *  is in flight AND this item is a legal target for it. Drives the
   *  pulsing halo in skin-components.css (reuses `sk-unassigned-breathe`).
   *  The dim on every OTHER item is a plain CSS descendant rule off
   *  `.shell[data-pick-mode]` - see app.css - not computed per-item here. */
  const pickable = () => pickButtonForDisplayId() !== null && props.item.kind === 'button';

  return (
    <div
      ref={wrapperRef}
      class="editable-item"
      data-kind={props.item.kind}
      data-item-id={props.item.id}
      data-editing={isEditMode() ? 'true' : 'false'}
      data-dragging={dragging() ? 'true' : 'false'}
      data-resizing={resizing() ? 'true' : 'false'}
      data-selected={isSelected() ? 'true' : 'false'}
      data-grabbed={grabbed() ? 'true' : 'false'}
      data-move-armed={moveArmed() ? 'true' : undefined}
      data-menu-open={menuOpen() ? 'true' : undefined}
      data-group-dragging={following() ? 'true' : undefined}
      data-settling={dragShift()?.settling ? 'true' : undefined}
      data-blocked={blockedNow() ? 'true' : undefined}
      data-just-added={isJustAdded(props.item.id) ? 'true' : undefined}
      data-shape={buttonShape() ?? undefined}
      data-hue={itemHue()}
      data-unassigned={unassigned() ? 'true' : undefined}
      data-midi-clash={midiClash() ? 'true' : undefined}
      data-pickable={pickable() ? 'true' : undefined}
      // The timeline lights the canvas (DEC-269): lit by a running timeline
      // (the TIMELINE mark below), held lit by the flash-safety rule, and a
      // count-in ring filling before the timeline's next fire on this item.
      data-tl-lit={timelineLit(props.item.id) ? 'true' : undefined}
      data-tl-fast={timelineFast(props.item.id) ? 'true' : undefined}
      data-tl-cue={countInProgress(props.item.id) !== undefined ? 'true' : undefined}
      onPointerDown={onWrapperPointerDown}
      onPointerEnter={onWrapperPointerEnter}
      onPointerLeave={onWrapperPointerLeave}
      onContextMenu={onWrapperContextMenu}
      style={{
        left: `${x()}px`,
        top: `${y()}px`,
        width: `${w()}px`,
        height: `${h()}px`,
        ...(buttonRotation() !== 0 ? { transform: `rotate(${buttonRotation()}deg)` } : {}),
        // A drag moves the item on the compositor, not through layout
        // (2026-10-10). `translate` composes with the rotation above.
        ...(dragShift() ? { translate: `${dragShift()!.dx}px ${dragShift()!.dy}px` } : {}),
        // Per-item colour (DEC-127), set ONCE here so every one of the 18
        // kinds inherits it - the wrapper is the only element every kind is
        // guaranteed to have. --hue-ink is paired with --hue because
        // color-mix() with `transparent` yields a half-invisible colour, not
        // the untinted one, so anything painting a FLAT colour (an icon, a
        // clip spine) needs the un-tinted fallback spelled out.
        '--hue': HUE_VALUES[itemHue()],
        '--hue-ink': itemHue() === 'none' ? 'var(--sk-ink)' : HUE_VALUES[itemHue()],
        // How full the count-in ring is, 0..1 (DEC-269).
        '--tl-cue': String(countInProgress(props.item.id) ?? 0),
      }}
    >
      {props.children}

      {/* Lit by the timeline, not a finger (DEC-269). */}
      <Show when={timelineLit(props.item.id)}>
        <span class="tl-mark" aria-hidden="true">Timeline</span>
      </Show>

      {/* Where it is (dragging) or how big (resizing), above the item so a
          finger on the iPad does not cover it. */}
      <Show when={dragging() || resizing()}>
        <div class="editable-readout" data-below={y() + (dragShift()?.dy ?? 0) < 34 ? 'true' : undefined}>
          {resizing()
            ? `${Math.round(w())} × ${Math.round(h())}`
            : `${Math.round(props.item.x + (dragShift()?.dx ?? 0))}, ${Math.round(props.item.y + (dragShift()?.dy ?? 0))}`}
        </div>
      </Show>

      <Show when={isEditMode() && editOwnsGesture(props.item.id)}>
        {/* Resize hit zones — 8 invisible strips (4 edges + 4 corners).
            Sides drive one axis, corners drive both. Cursor changes on
            hover. Hidden for ButtonGrid (rows/cols steppers instead).
            Gated by editOwnsGesture so in "triggers in edit mode" an
            un-grabbed item has no handles (its edges fire the control);
            handles appear once it's hover-grabbed.
            The explicit `isEditMode()` is what keeps a menu-armed Move in
            TEST mode to moving only: `editOwnsGesture` is now true for an
            armed item in any mode, and growing resize handles around a live
            control mid-set is not what "Move" asked for. */}
        <Show when={props.item.kind !== 'buttongrid' && props.item.kind !== 'mixer'}>
          <For each={RESIZE_DIRS}>
            {(dir) => (
              <div
                class="editable-resize-zone"
                data-dir={dir}
                onPointerDown={makeResizePointerDown(dir)}
                aria-label={`Resize ${dir}`}
              />
            )}
          </For>
        </Show>
      </Show>
    </div>
  );
}
